<script setup lang="ts">
import SparkleImage from '@/partners/lingo/lingo-giveaway/assets/images/prize_sparkle.png'
import BaseButton from '@/components/base/BaseButton.vue'
import { computed, ref } from 'vue'
import { prizes, prizeSchedule } from '@/partners/lingo/lingo-giveaway/config/prizes'
import { T } from '@tolgee/vue'
import { middleTruncate } from '@/helpers/utils'
import { useLingoGiveaway } from '@/partners/lingo/lingo-giveaway/composables/lingo-giveaway'

defineProps<{
  hasPrizes: boolean
}>()
defineEmits<{
  (e: 'onCheck'): void
  (e: 'onPrizes'): void
}>()

const { raffleId, raffleWinners } = useLingoGiveaway()
const prize = ref(prizes[prizeSchedule[raffleId.value]])
const hasWinners = computed<boolean>(() => !!Object.keys(raffleWinners.value).length)

</script>
<template>
  <div class="w-full flex flex-1 flex-col items-center gap-y-8 h-full overflow-hidden">
    <div class="w-full flex flex-col h-full gap-y-6 justify-center items-center">
      <div
        class="relative size-[120px] min-h-[120px] overflow-hidden rounded-xl border border-[#FFCB00]"
      >
        <div
          class="absolute w-full h-full rounded-xl bg-gradient-to-b from-[#FEE06C] to-[#FBD753] opacity-50"
        />
        <div class="absolute top-0 left-0 rounded-tl-xl rounded-br-xl py-1.5 px-2 gold-background z-[30] h-max">
          <span class="block uppercase text-black text-[10px] leading-[10px] font-bold h-[10px]">
            <T
              key-name="lingo.giveaway.winners.today_prize"
              default-value="Today's prize"
            />
          </span>
        </div>
        <div
          class="absolute rounded-xl"
        >
          <img
            class="mix-blend-plus-lighter"
            :src="SparkleImage"
          >
        </div>

        <div class="w-full h-full flex items-center justify-center p-4">
          <img
            class="relative z-10 size-full"
            :src="prize.src"
            :alt="prize.name"
          >
        </div>
      </div>

      <h2 class="text-2xl font-bold uppercase -tracking-[0.48px] text-light-white">
        <T
          key-name="lingo.giveaway.winners.prize_draw"
          default-value="Prize draw"
        />
      </h2>
    </div>
    <div
      v-if="hasWinners"
      class="w-full flex flex-col gap-y-3 h-full flex-1 overflow-hidden"
    >
      <p>
        <T
          key-name="lingo.giveaway.winners.recent_winners"
          default-value="Recent winners"
        />
      </p>
      <div class="flex-1 overflow-hidden w-full h-full flex flex-col">
        <div class="flex flex-1 flex-col w-full h-full gap-y-3 overflow-y-auto pb-16 scrollable-container">
          <div
            v-for="(day, index) in Object.keys(raffleWinners).reverse()"
            :key="index"
            class="py-2 pl-3 pr-1.5 bg-dark-top-layer rounded-xl flex justify-between items-center"
          >
            <p class="text-base font-semibold text-white">
              {{ middleTruncate(raffleWinners[+day as keyof typeof raffleWinners], 11) }}
            </p>
            <div class="size-12 min-w-12">
              <img
                class="h-full w-full"
                :src="prizes[prizeSchedule[+day as keyof typeof prizeSchedule]].src"
                :alt="prizes[prizeSchedule[+day as keyof typeof prizeSchedule]].name"
              >
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="flex-1 w-full h-full justify-center items-center"
    >
      <p class="text-lg font-bold text-white text-center">
        <T
          key-name="lingo.giveaway.winners.empty"
          default-value="Winners will be announced shortly"
        />
      </p>
    </div>
    <div
      class="flex flex-col gap-y-3 w-full sticky pb-8 bottom-0 z-[10]"
    >
      <BaseButton
        class="w-full"
        :disabled="!hasWinners"
        style-type="secondary"
        @click="$emit('onCheck')"
      >
        <T
          key-name="lingo.giveaway.winners.cta"
          default-value="Check your status"
        />
      </BaseButton>
      <BaseButton
        v-if="hasPrizes"
        class="w-full"
        style-type="secondary"
        @click="$emit('onPrizes')"
      >
        <T
          key-name="lingo.giveaway.winners.past_wins_cta"
          default-value="Check your past wins"
        />
      </BaseButton>
      <p class="text-center px-2.5 font-medium text-base text-white/60 z-10">
        <T
          key-name="lingo.giveaway.winners.claim_info"
          default-value="You have 30 days to claim your prize from the moment the draw was made."
        />
      </p>
    </div>
  </div>
  <div
    v-if="hasWinners"
    class="absolute w-full h-[50%] bottom-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_11.49%,#000000_47.55%)] pointer-events-none"
  />
</template>
<style scoped>
.gold-background {
  background: linear-gradient(274deg, rgba(204, 158, 54, 0.50) -15.81%, rgba(255, 255, 159, 0.50) 101.07%, rgba(204, 161, 59, 0.50) 138.61%);
}

.scrollable-container {
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
}

.scrollable-container::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}
</style>